import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { ServicesMegaMenu } from './ServicesMegaMenu';
import { megaMenuCategories, services } from '../data/serviceCatalog';

const ERP_URL = 'https://utkarshudyam.in/utkarshudyam-erp';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(null);
  const closeTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Delayed close so users can travel from button to dropdown without it snapping shut.
  const openMenu = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } setServicesOpen(true); };
  const scheduleClose = () => { if (closeTimer.current) clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setServicesOpen(false), 260); };

  const closeAll = () => { setIsMobileMenuOpen(false); setServicesOpen(false); setMobileCatOpen(null); };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Blog', path: '/blog' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-md'}`}>
      {/* Top premium contact bar */}
      <div className="bg-gradient-to-r from-[#0A2463] to-[#123a8f] text-white">
        <div className="container mx-auto px-4 py-2">
          <div className="flex justify-between items-center text-xs md:text-sm gap-2">
            <div className="flex items-center gap-4 md:gap-6">
              <a href="tel:9755085383" className="flex items-center gap-1.5 hover:text-[#FFB703] transition-colors font-semibold">
                <Phone size={13} /> <span>9755085383</span>
              </a>
              <a href="mailto:Sales@utkarshudyam.com" className="hidden sm:flex items-center gap-1.5 hover:text-[#FFB703] transition-colors">
                <Mail size={13} /> <span>Sales@utkarshudyam.com</span>
              </a>
            </div>
            <div className="text-[11px] md:text-sm flex items-center gap-1 md:gap-3 whitespace-nowrap">
              <span>⭐ 5.0 Google Rated</span>
              <span className="hidden sm:inline text-white/40">|</span>
              <span className="hidden sm:inline">DPIIT Certified Startup</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center gap-3 group">
            <img src="/company-logo.png" alt="Utkarshudyam Solution" className="h-14 md:h-16 w-auto object-contain group-hover:scale-105 transition-transform" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            <Link to="/" className={`px-3 py-2 text-sm font-semibold transition-colors ${location.pathname === '/' ? 'text-[#0A2463]' : 'text-gray-600 hover:text-[#0A2463]'}`}>Home</Link>

            {/* Services dropdown with click-safe hover behaviour */}
            <div className="relative" onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
              <button
                data-testid="services-nav-btn"
                aria-expanded={servicesOpen}
                onClick={() => setServicesOpen((v) => !v)}
                className={`px-3 py-2 text-sm font-semibold flex items-center gap-1 transition-colors ${location.pathname.startsWith('/services') ? 'text-[#0A2463]' : 'text-gray-600 hover:text-[#0A2463]'}`}
              >
                Services <ChevronDown size={14} className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {servicesOpen && (
                <div onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
                  <ServicesMegaMenu onNavigate={closeAll} />
                </div>
              )}
            </div>

            {navLinks.slice(1).map((link) => (
              <Link key={link.path} to={link.path}
                className={`px-3 py-2 text-sm font-semibold transition-colors ${location.pathname === link.path ? 'text-[#0A2463]' : 'text-gray-600 hover:text-[#0A2463]'}`}>
                {link.name}
              </Link>
            ))}

            <div className="w-px h-6 bg-gray-200 mx-2" />

            {/* Utkarshudyam ERP external link (replaces Admin & Invoice Generator) */}
            <a href={ERP_URL} target="_blank" rel="noreferrer" data-testid="header-erp-btn">
              <Button className="ml-1 bg-gradient-to-r from-[#0A2463] to-[#123a8f] hover:from-[#123a8f] hover:to-[#0A2463] text-white shadow-md hover:shadow-lg transition-all">
                Utkarshudyam ERP <ExternalLink size={14} className="ml-1.5" />
              </Button>
            </a>

            <a href="tel:9755085383">
              <Button className="ml-2 bg-gradient-to-r from-[#FFB703] to-[#FB8500] hover:from-[#FB8500] hover:to-[#FFB703] text-white shadow-md hover:shadow-lg transition-all">
                Get Free Consultation
              </Button>
            </a>
          </nav>

          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 text-[#0A2463]">
            {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-1">
            <Link to="/" onClick={closeAll} className="py-3 border-b border-gray-100 font-semibold text-[#0A2463]">Home</Link>

            <div className="border-b border-gray-100">
              <button onClick={() => setMobileCatOpen(mobileCatOpen === '__' ? null : '__')} data-testid="mobile-services-toggle"
                className="w-full py-3 flex items-center justify-between font-semibold text-[#0A2463]">
                Services <ChevronDown size={16} className={`transition-transform ${mobileCatOpen === '__' ? 'rotate-180' : ''}`} />
              </button>
              {mobileCatOpen === '__' && (
                <div className="pb-3">
                  {megaMenuCategories.map((cat) => {
                    const openCat = mobileCatOpen === cat.id;
                    return (
                      <div key={cat.id} className="border-t border-gray-100">
                        <button className="w-full py-2.5 flex items-center justify-between text-sm font-semibold text-gray-700"
                          onClick={() => setMobileCatOpen(openCat ? '__' : cat.id)}>
                          {cat.label}
                          <ChevronRight size={14} className={`transition-transform ${openCat ? 'rotate-90' : ''}`} />
                        </button>
                        {openCat && (
                          <ul className="pl-3 pb-2 space-y-1">
                            {cat.slugs.map((slug) => {
                              const s = services[slug]; if (!s) return null;
                              return (
                                <li key={slug}>
                                  <Link onClick={closeAll} to={`/services/${slug}`} className="text-sm text-gray-600 py-1.5 block">
                                    • {s.title}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                  <Link onClick={closeAll} to="/services" className="text-sm font-bold text-[#FB8500] py-2 block">View all services →</Link>
                </div>
              )}
            </div>

            {navLinks.slice(1).map((link) => (
              <Link key={link.path} to={link.path} onClick={closeAll} className="py-3 border-b border-gray-100 font-semibold text-[#0A2463]">
                {link.name}
              </Link>
            ))}

            <a href={ERP_URL} target="_blank" rel="noreferrer" onClick={closeAll} data-testid="mobile-erp-btn"
              className="mt-3 block">
              <Button className="w-full bg-gradient-to-r from-[#0A2463] to-[#123a8f] text-white flex items-center justify-center gap-2">
                Utkarshudyam ERP <ExternalLink size={14} />
              </Button>
            </a>
            <a href="tel:9755085383" className="mt-2 block">
              <Button className="w-full bg-gradient-to-r from-[#FFB703] to-[#FB8500] text-white">Get Free Consultation</Button>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
